import type { Task } from "../records";
import { requestedDocumentRequirementKey } from "./document-requirements";
import { effectiveRequirements, type RequirementEvaluationInput } from "./effective-requirements";
import { type ChecklistRequirement, isRequirementDismissable } from "./requirement-policy";

export interface RequirementStatus {
  requirement: ChecklistRequirement;
  satisfied: boolean;
  /** Concluded as a nonexistent document rather than completed. */
  waived: boolean;
}

export const evaluateRequirements = (input: RequirementEvaluationInput): RequirementStatus[] =>
  effectiveRequirements(input).map((requirement) => {
    const waived =
      isRequirementDismissable(requirement) && requirement.key in (input.progress?.waived ?? {});
    const satisfied = waived || requirement.key in (input.progress?.satisfied ?? {});
    return { requirement, waived, satisfied };
  });

/** Required rows not yet satisfied, then groups below their minimum; empty means completable. */
export const unsatisfiedRequirementKeys = (input: RequirementEvaluationInput): string[] => {
  const statuses = evaluateRequirements(input);
  const pending = statuses
    .filter(({ requirement, satisfied }) => !satisfied && requirement.required)
    .map(({ requirement }) => requirement.key);
  const pendingGroups = (input.groups ?? [])
    .filter((group) => {
      const members = statuses.filter(
        ({ requirement }) =>
          requirement.kind === "checklist_item" && requirement.groupId === group.id,
      );
      const ticked = members.filter(({ satisfied }) => satisfied).length;
      return members.length > 0 && ticked < group.minimumSatisfied;
    })
    .map(({ id }) => id);
  return [...pending, ...pendingGroups];
};

/** Derives the document-collection outcome from dismissed checklist rows. Rows marked
 * `required: false` (e.g. documents only recommended for a later stage) can be waived
 * without turning the task's own outcome into an escalation. */
export const documentsCollectionSolution = (
  task: Pick<Task, "requestedDocuments" | "requirementProgress">,
) =>
  task.requestedDocuments.some(
    (document) =>
      document.required !== false &&
      requestedDocumentRequirementKey(document) in task.requirementProgress.waived,
  )
    ? "documents_unavailable"
    : "documents_provided";
